import {
  useMutation,
  useQuery,
  useQueryClient,
  type QueryClient,
  keepPreviousData,
} from '@tanstack/react-query';
import type { ReportDefinition } from '@ikomet/shared';
import { api, del, get, patch, post, put } from './api';
import { useAuthStore } from '@/stores/authStore';
import { todayInZone } from '@/lib/format';
import type {
  ActivityEntry,
  ApprovalRow,
  Attachment,
  BoardResponse,
  Burndown,
  Client,
  Comment,
  ExecutiveDashboard,
  MyDashboard,
  Notification,
  Paginated,
  Person,
  Project,
  ProjectDashboard,
  ProjectTemplate,
  SearchHit,
  Sprint,
  Task,
  TimeEntry,
  Timesheet,
  UserRef,
  UtilisationRow,
  Workspace,
} from './types';

/** Central key registry — every invalidation in the app refers to these. */
export const qk = {
  me: ['me'] as const,
  workspaces: ['workspaces'] as const,
  projects: (filters?: unknown) => ['projects', filters ?? {}] as const,
  project: (id: string) => ['project', id] as const,
  board: (projectId: string, filters?: unknown) => ['board', projectId, filters ?? {}] as const,
  tasks: (filters?: unknown) => ['tasks', filters ?? {}] as const,
  task: (id: string) => ['task', id] as const,
  subtasks: (id: string) => ['subtasks', id] as const,
  sprints: (projectId: string) => ['sprints', projectId] as const,
  burndown: (sprintId: string) => ['burndown', sprintId] as const,
  velocity: (projectId: string) => ['velocity', projectId] as const,
  phases: (projectId: string) => ['phases', projectId] as const,
  comments: (entityType: string, entityId: string) => ['comments', entityType, entityId] as const,
  attachments: (entityType: string, entityId: string) =>
    ['attachments', entityType, entityId] as const,
  activity: (entityType: string, entityId: string) => ['activity', entityType, entityId] as const,
  directory: ['directory'] as const,
  clients: ['clients'] as const,
  notifications: ['notifications'] as const,
  timer: ['timer'] as const,
  timesheet: (weekStart: string, userId?: string) => ['timesheet', weekStart, userId] as const,
  timeEntries: (filters?: unknown) => ['timeEntries', filters ?? {}] as const,
  dashboardMe: ['dashboard', 'me'] as const,
  dashboardExec: ['dashboard', 'executive'] as const,
  dashboardProject: (id: string) => ['dashboard', 'project', id] as const,
  utilisation: (from: string, to: string) => ['utilisation', from, to] as const,
  search: (term: string) => ['search', term] as const,
  taskTree: (filters?: unknown) => ['taskTree', filters ?? {}] as const,
  people: (filters?: unknown) => ['people', filters ?? {}] as const,
  templates: (category?: string) => ['templates', category ?? 'all'] as const,
  template: (id: string) => ['template', id] as const,
  approvals: (filters?: unknown) => ['approvals', filters ?? {}] as const,
  approvalDetail: (userId: string, week: string) => ['approvalDetail', userId, week] as const,
  taskLinks: (taskId: string) => ['taskLinks', taskId] as const,
};

// ---------- Directory & workspaces ----------

/**
 * Which sign-in methods this deployment offers.
 *
 * Asked rather than assumed, so the Microsoft button only appears where it is
 * actually configured — a button that can only fail is worse than no button.
 */
export const useAuthMethods = () =>
  useQuery({
    queryKey: ['authMethods'],
    queryFn: () => get<{ password: boolean; microsoft: boolean }>('/auth/methods'),
    staleTime: Infinity,
  });

/**
 * People for pickers and mentions, inside the active workspace.
 *
 * `scope: 'organization'` widens it to everyone in the tenant — which the
 * "add someone to this workspace" dialog needs, since a picker limited to
 * current members can only offer the people you already have.
 */
export interface Organization {
  id: string;
  name: string;
  timezone?: string;
  /** Which notification types also send email, keyed by type. */
  notificationSettings?: Record<string, { email: boolean }>;
}

export const useOrganization = () =>
  useQuery({
    queryKey: ['organization'],
    queryFn: () => get<Organization>('/organization'),
    staleTime: 5 * 60 * 1000,
  });

export const useUpdateOrganization = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => patch<Organization>('/organization', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['organization'] }),
  });
};

export const useDirectory = (scope?: 'organization') =>
  useQuery({
    queryKey: [...qk.directory, scope ?? 'workspace'],
    queryFn: () => get<UserRef[]>('/users/directory', scope ? { scope } : undefined),
    // The people list changes rarely; avoid refetching it on every screen.
    staleTime: 5 * 60 * 1000,
  });

export const useWorkspaces = () =>
  useQuery({ queryKey: qk.workspaces, queryFn: () => get<Workspace[]>('/workspaces') });

export const useClients = () =>
  useQuery({
    queryKey: qk.clients,
    queryFn: () => get<Paginated<{ id: string; name: string; code?: string; color: string }>>(
      '/clients',
      { pageSize: 200 },
    ),
    staleTime: 5 * 60 * 1000,
  });

// ---------- Projects ----------

export interface ProjectFilters {
  workspaceId?: string;
  status?: string;
  health?: string;
  search?: string;
  mineOnly?: boolean;
  page?: number;
  pageSize?: number;
}

export const useProjects = (filters: ProjectFilters = {}, enabled = true) =>
  useQuery({
    queryKey: qk.projects(filters),
    queryFn: () => get<Paginated<Project>>('/projects', { pageSize: 50, ...filters }),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useProject = (id?: string) =>
  useQuery({
    queryKey: qk.project(id!),
    queryFn: () => get<Project>(`/projects/${id}`),
    enabled: !!id,
  });

export const useCreateProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<Project>('/projects', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
};

export const useUpdateProject = (id: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => patch<Project>(`/projects/${id}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.project(id) });
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

/** Upserts a project member — the API updates access/allocation if already present. */
export const useAddProjectMember = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      userId: string;
      access?: string;
      role?: string;
      allocationPercent?: number;
    }) =>
      post<Project>(`/projects/${projectId}/members`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.project(projectId) });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useRemoveProjectMember = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (userId: string) => del<Project>(`/projects/${projectId}/members/${userId}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.project(projectId) });
      qc.invalidateQueries({ queryKey: ['projects'] });
    },
  });
};

export const useAddRisk = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      post<Project>(`/projects/${projectId}/risks`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.project(projectId) }),
  });
};

export const useMilestoneDecision = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ milestoneId, decision }: { milestoneId: string; decision: string }) =>
      patch<Project>(`/projects/${projectId}/milestones/${milestoneId}/${decision}`),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.project(projectId) });
      qc.invalidateQueries({ queryKey: qk.dashboardProject(projectId) });
    },
  });
};

// ---------- Tasks ----------

export interface TaskFilters {
  projectId?: string;
  sprintId?: string;
  /** Comma-separated phase ids; `none` means not assigned to a phase. */
  phaseId?: string;
  /** Comma-separated lifecycle categories — how "hide closed" is expressed. */
  statusCategory?: string;
  search?: string;
  status?: string;
  priority?: string;
  type?: string;
  assigneeIds?: string;
  tags?: string;
  mineOnly?: boolean;
  overdueOnly?: boolean;
  topLevelOnly?: boolean;
  page?: number;
  pageSize?: number;
  sortBy?: string;
  sortDir?: 'asc' | 'desc';
}

export const useTasks = (filters: TaskFilters = {}, enabled = true) =>
  useQuery({
    queryKey: qk.tasks(filters),
    queryFn: () => get<Paginated<Task>>('/tasks', filters),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useBoard = (projectId?: string, filters: TaskFilters = {}) =>
  useQuery({
    queryKey: qk.board(projectId!, filters),
    queryFn: () => get<BoardResponse>(`/tasks/board/${projectId}`, filters),
    enabled: !!projectId,
    placeholderData: keepPreviousData,
  });

export const useTask = (id?: string) =>
  useQuery({
    queryKey: qk.task(id!),
    queryFn: () => get<Task>(`/tasks/${id}`),
    enabled: !!id,
  });

export const useSubtasks = (id?: string) =>
  useQuery({
    queryKey: qk.subtasks(id!),
    queryFn: () => get<Task[]>(`/tasks/${id}/subtasks`),
    enabled: !!id,
  });

// ---------- Defect links ----------

export interface TaskLinkRef {
  id: string;
  key: string;
  title: string;
  type: string;
  status: string;
  statusCategory: string;
  priority: string;
  estimatedHours?: number;
  linkType?: string;
  bug?: { severity?: string; resolution?: string };
}

export interface TaskLinks {
  /** True when this task is itself a defect. */
  isBug: boolean;
  /** Tasks this defect was raised against, or is fixed by. */
  linkedTasks: TaskLinkRef[];
  /** Defects raised against this task. */
  linkedBugs: TaskLinkRef[];
}

export const useTaskLinks = (taskId?: string) =>
  useQuery({
    queryKey: qk.taskLinks(taskId!),
    queryFn: () => get<TaskLinks>(`/tasks/${taskId}/links`),
    enabled: !!taskId,
  });

/**
 * Invalidates both ends of a link.
 *
 * The edge is stored on the defect, but the task on the other side renders the
 * reverse direction, so a stale cache there would show a link that no longer
 * exists (or miss one that now does).
 */
const useLinkMutation = <TPayload,>(
  request: (subjectId: string, payload: TPayload) => Promise<unknown>,
) => {
  const qc = useQueryClient();
  // `id` is the record being acted on; a payload may carry its own `taskId`
  // for the other end of the link, so the two must not share a name.
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      request(id, payload as TPayload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['taskLinks'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['taskTree'] });
      qc.invalidateQueries({ queryKey: ['board'] });
    },
  });
};

/** Raises a defect against a task, linking in the same call. */
export const useReportBug = () =>
  useLinkMutation<Record<string, unknown>>((taskId, payload) =>
    post<Task>(`/tasks/${taskId}/bugs`, payload),
  );

/** The other direction: creates the fix task for a defect. */
export const useCreateFixTask = () =>
  useLinkMutation<Record<string, unknown>>((bugId, payload) =>
    post<Task>(`/tasks/${bugId}/fix-task`, payload),
  );

export const useLinkBug = () =>
  useLinkMutation<Record<string, unknown>>((bugId, payload) =>
    post<Task>(`/tasks/${bugId}/links`, payload),
  );

export const useUnlinkBug = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ bugId, taskId }: { bugId: string; taskId: string }) =>
      del(`/tasks/${bugId}/links/${taskId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['taskLinks'] }),
  });
};

/**
 * Everything a task change can make stale.
 *
 * One list, used by every task mutation, because the alternative is what was
 * here: each mutation named its own caches, `taskTree` was in none of them, and
 * the Backlog silently kept showing a list from before the change until someone
 * reloaded the page. A cache that is only refreshed by the mutations that
 * happened to remember it is a cache that goes stale.
 *
 * Prefixes, not exact keys: these are all keyed by filters too, and a view with
 * a different filter set holds a different key for the same data.
 */
const TASK_DERIVED_CACHES = [
  'board',
  'tasks',
  // The hierarchical list and the Backlog. Missing from every mutation until
  // adding a task from the list view failed to show up in the table view.
  'taskTree',
  'subtasks',
  'dashboard',
  // Phase cards carry task counts and effort totals.
  'phases',
  // The Activity tab is a reading of the change that was just made, so it goes
  // stale the instant one is. It was in no list at all: every edit was recorded
  // and none of them appeared until the drawer was closed and opened again.
  'activity',
] as const;

const invalidateTaskCaches = (qc: QueryClient) => {
  for (const key of TASK_DERIVED_CACHES) qc.invalidateQueries({ queryKey: [key] });
};

export const useCreateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<Task>('/tasks', payload),
    onSuccess: () => invalidateTaskCaches(qc),
  });
};

export const useUpdateTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<Task>(`/tasks/${id}`, payload),
    onSuccess: (task) => {
      qc.invalidateQueries({ queryKey: qk.task(task.id) });
      invalidateTaskCaches(qc);
    },
  });
};

/**
 * Board drag. The card is moved in the cache immediately and rolled back if the
 * server rejects it, so dragging never shows a visible round-trip.
 */
export const useMoveTask = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      id,
      ...payload
    }: {
      id: string;
      status?: string;
      beforeTaskId?: string;
      afterTaskId?: string;
      parentTaskId?: string | null;
    }) => patch<Task>(`/tasks/${id}/move`, payload),

    onMutate: async ({ id, status }) => {
      await qc.cancelQueries({ queryKey: ['board', projectId] });
      const snapshots = qc.getQueriesData<BoardResponse>({ queryKey: ['board', projectId] });

      for (const [key, board] of snapshots) {
        if (!board) continue;
        let moved: Task | undefined;
        const stripped = board.columns.map((column) => ({
          ...column,
          tasks: column.tasks.filter((task) => {
            if (task.id !== id) return true;
            moved = task;
            return false;
          }),
        }));
        if (!moved) continue;

        qc.setQueryData<BoardResponse>(key, {
          ...board,
          columns: stripped.map((column) =>
            column.status.key === status
              ? { ...column, tasks: [...column.tasks, { ...moved!, status }] }
              : column,
          ),
        });
      }
      return { snapshots };
    },

    onError: (_err, _vars, context) => {
      for (const [key, data] of context?.snapshots ?? []) qc.setQueryData(key, data);
    },

    // A list drag can re-parent, which changes depth and ordering for a whole
    // subtree — only a refetch of the tree reflects that.
    onSettled: () => invalidateTaskCaches(qc),
  });
};

export const useBulkUpdateTasks = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      patch<{ updated: number }>('/tasks/bulk', payload),
    onSuccess: () => invalidateTaskCaches(qc),
  });
};

/**
 * Relates one work item to another, in both directions at once.
 *
 * The API writes the inverse on the far task, so both are invalidated here —
 * leaving the other one cached is how a link appears on one side of a pair and
 * not the other until a reload.
 */
export const useLinkTask = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { taskId: string; type: string }) =>
      post<Task>(`/tasks/${taskId}/dependencies`, payload),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: qk.task(taskId) });
      qc.invalidateQueries({ queryKey: qk.task(variables.taskId) });
      invalidateTaskCaches(qc);
    },
  });
};

export const useUnlinkTask = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ otherTaskId, type }: { otherTaskId: string; type: string }) =>
      del(`/tasks/${taskId}/dependencies/${otherTaskId}?type=${encodeURIComponent(type)}`),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: qk.task(taskId) });
      qc.invalidateQueries({ queryKey: qk.task(variables.otherTaskId) });
      invalidateTaskCaches(qc);
    },
  });
};

export const useDeleteTask = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/tasks/${id}`),
    onSuccess: () => invalidateTaskCaches(qc),
  });
};

export const useToggleChecklistItem = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ itemId, done }: { itemId: string; done: boolean }) =>
      patch<Task>(`/tasks/${taskId}/checklist/${itemId}`, { done }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.task(taskId) }),
  });
};

export const useRemoveChecklistItem = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemId: string) => del<Task>(`/tasks/${taskId}/checklist/${itemId}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.task(taskId) }),
  });
};

/** Whole-list reorder after a drag; the API renumbers atomically. */
export const useReorderChecklist = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (itemIds: string[]) => patch<Task>(`/tasks/${taskId}/checklist`, { itemIds }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.task(taskId) }),
  });
};

export const useAddChecklistItem = (taskId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (label: string) => post<Task>(`/tasks/${taskId}/checklist`, { label }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.task(taskId) }),
  });
};

// ---------- Sprints ----------

export const useSprints = (projectId?: string) =>
  useQuery({
    queryKey: qk.sprints(projectId!),
    queryFn: () => get<Sprint[]>('/sprints', { projectId }),
    enabled: !!projectId,
  });

export const useBurndown = (sprintId?: string) =>
  useQuery({
    queryKey: qk.burndown(sprintId!),
    queryFn: () => get<Burndown>(`/sprints/${sprintId}/burndown`),
    enabled: !!sprintId,
  });

export const useCreateSprint = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      post<Sprint>('/sprints', { projectId, ...payload }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sprints(projectId) }),
  });
};

export interface Velocity {
  averageVelocity: number;
  sprints: Array<{ id: string; name: string; committedPoints: number; completedPoints: number }>;
}

export const useVelocity = (projectId?: string) =>
  useQuery({
    queryKey: qk.velocity(projectId!),
    queryFn: () => get<Velocity>(`/sprints/velocity/${projectId}`),
    enabled: !!projectId,
  });

export const useUpdateSprint = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<Sprint>(`/sprints/${id}`, payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.sprints(projectId) }),
  });
};

/**
 * Closes a sprint and sweeps whatever did not finish.
 *
 * Carry-over is an explicit choice at close time rather than an automatic
 * rollover: silently dragging unfinished work into the next sprint is how a
 * sprint boundary stops meaning anything.
 */
export const useCompleteSprint = (projectId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      post<Sprint>(`/sprints/${id}/complete`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.sprints(projectId) });
      qc.invalidateQueries({ queryKey: qk.velocity(projectId) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['taskTree'] });
      qc.invalidateQueries({ queryKey: ['board', projectId] });
    },
  });
};

// ---------- Phases (stage gates) ----------

export interface ProjectPhase {
  /**
   * The API maps this explicitly. It used to be declared `_id` here and never
   * sent at all, so every phase action built a URL ending in `undefined`.
   */
  id: string;
  key: string;
  name: string;
  description?: string;
  order: number;
  color: string;
  status: string;
  requiresApproval: boolean;
  blockedByPrevious: boolean;
  plannedStartDate?: string;
  plannedEndDate?: string;
  actualStartDate?: string;
  actualEndDate?: string;
  decidedAt?: string;
  decisionNote?: string;
  rejectionCount: number;
  exitCriteria?: string[];
  taskCount: number;
  doneCount: number;
  openCount: number;
  estimatedHours: number;
  actualHours: number;
  /** Set when an earlier gate has not cleared yet. */
  blockedBy: { key: string; name: string } | null;
}

export const usePhases = (projectId?: string) =>
  useQuery({
    queryKey: qk.phases(projectId!),
    queryFn: () =>
      get<{ usesPhases: boolean; phases: ProjectPhase[]; unassignedTaskCount: number }>(
        `/projects/${projectId}/phases`,
      ),
    enabled: !!projectId,
  });

/**
 * A gate transition touches the plan and the work behind it, so both caches go.
 */
const usePhaseMutation = <TArgs,>(
  projectId: string,
  request: (args: TArgs) => Promise<unknown>,
) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.phases(projectId) });
      qc.invalidateQueries({ queryKey: qk.project(projectId) });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['taskTree'] });
    },
  });
};

export const useApplyDefaultPhases = (projectId: string) =>
  usePhaseMutation<void>(projectId, () => post(`/projects/${projectId}/phases/defaults`, {}));

export const useStartPhase = (projectId: string) =>
  usePhaseMutation<string>(projectId, (phaseId) =>
    post(`/projects/${projectId}/phases/${phaseId}/start`, {}),
  );

export const useSubmitPhase = (projectId: string) =>
  usePhaseMutation<string>(projectId, (phaseId) =>
    post(`/projects/${projectId}/phases/${phaseId}/submit`, {}),
  );

export const useDecidePhase = (projectId: string) =>
  usePhaseMutation<{ phaseId: string; decision: string; note?: string }>(
    projectId,
    ({ phaseId, ...body }) => post(`/projects/${projectId}/phases/${phaseId}/decide`, body),
  );

export const useSkipPhase = (projectId: string) =>
  usePhaseMutation<{ phaseId: string; note?: string }>(projectId, ({ phaseId, ...body }) =>
    post(`/projects/${projectId}/phases/${phaseId}/skip`, body),
  );

export const useReplacePhases = (projectId: string) =>
  usePhaseMutation<{ phases: unknown[] }>(projectId, (body) =>
    put(`/projects/${projectId}/phases`, body),
  );

export interface TimeDrilldownFilters {
  userId?: string;
  projectId?: string;
  taskId?: string;
  from?: string;
  to?: string;
  isBillable?: boolean;
}

export interface ReviewItem {
  id: string;
  key: string;
  title: string;
  project: string;
  dueDate?: string;
  completedAt?: string;
  daysLate: number | null;
  estimatedHours: number;
  actualHours: number;
  reopenCount: number;
}

export interface PersonReview {
  person: {
    id: string;
    name: string;
    jobTitle: string;
    employmentType: string;
    avatarColor?: string;
  };
  period: { from: string; to: string };
  headline: {
    delivered: number;
    dated: number;
    late: number;
    onTimePercent: number | null;
    estimated: number;
    estimateAccuracy: number | null;
    defects: number;
    reopened: number;
    reworkPercent: number | null;
    loggedHours: number;
    billableHours: number;
    billablePercent: number | null;
    capacityHours: number;
    utilisationPercent: number | null;
    hoursPerItem: number | null;
  };
  trend: Array<{ month: string; delivered: number; late: number; logged: number }>;
  projects: Array<{
    id: string;
    name: string;
    code: string;
    color?: string;
    hours: number;
    delivered: number;
  }>;
  lateItems: ReviewItem[];
  reopenedItems: ReviewItem[];
  notableItems: ReviewItem[];
  recentItems: ReviewItem[];
}

/** One person over one period — the page a manager opens during a review. */
export const usePersonReview = (userId?: string, range?: { from: string; to: string }) =>
  useQuery({
    queryKey: ['personReview', userId, range],
    queryFn: () => get<PersonReview>(`/reports/review/${userId}`, range),
    enabled: !!userId,
  });

export interface ReportTimeRow {
  id: string;
  date: string;
  person: string;
  project: string;
  projectCode: string;
  task: string;
  description: string;
  hours: number;
  state: 'billable' | 'awaiting_approval' | 'non_billable';
  status: string;
  revenue: number;
  cost: number;
}

/**
 * The entries behind a report figure.
 *
 * Served by the reports module, not the time module: it runs the same pipeline
 * that produced the figure, so the panel cannot disagree with the number that
 * opened it.
 */
export const useReportTimeDrilldown = (params?: Record<string, string>) =>
  useQuery({
    queryKey: ['reportTimeDrilldown', params ?? {}],
    queryFn: () =>
      get<{
        entries: ReportTimeRow[];
        count: number;
        totalHours: number;
        revenue: number;
        cost: number;
      }>('/reports/drilldown/time', params),
    enabled: !!params,
  });

export const useTimeDrilldown = (filters?: TimeDrilldownFilters) =>
  useQuery({
    queryKey: ['timeDrilldown', filters ?? {}],
    queryFn: () =>
      get<{
        entries: TimeEntry[];
        totalMinutes: number;
        billableMinutes: number;
        nonBillableMinutes: number;
      }>('/time-entries/drilldown', filters as Record<string, unknown>),
    enabled: !!filters,
  });

/**
 * Downloads the billing CSV through the authenticated client.
 *
 * A plain link cannot carry the bearer token, so the bytes come back as a blob
 * and are handed to the browser through an object URL that is revoked straight
 * after — leaving it attached would pin the whole file in memory.
 */
/**
 * The timesheet as a file.
 *
 * Two shapes, one download: `workbook` is the six-column sheet that goes to
 * the client, one tab per person; `csv` is the billing view, with rates,
 * amounts and approval state for whoever raises the invoice.
 */
export const useExportBilling = (format: 'workbook' | 'csv' = 'workbook') =>
  useMutation({
    mutationFn: async (filters: Record<string, unknown>) => {
      const workbook = format === 'workbook';
      const response = await api.get(
        workbook ? '/time-entries/timesheet.xlsx' : '/time-entries/billing.csv',
        { params: filters, responseType: 'blob' },
      );
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `timesheet-${new Date().toISOString().slice(0, 10)}.${workbook ? 'xlsx' : 'csv'}`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

/**
 * Self-service profile edit.
 *
 * Separate from the admin user PATCH: everyone may rename themselves, nobody
 * may give themselves a raise — so role, rates and capacity are not on this
 * path at all rather than being filtered out of it later.
 */
export const useUpdateMyProfile = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      patch<Record<string, unknown>>('/users/me', payload),
    onSuccess: (user) => {
      // The signed-in identity lives in the auth store, so the header avatar
      // and every "you" reference update without a reload.
      useAuthStore.getState().setUser(user as never);
      qc.invalidateQueries({ queryKey: qk.me });
      qc.invalidateQueries({ queryKey: qk.directory });
      qc.invalidateQueries({ queryKey: ['people'] });
    },
  });
};

export interface TeamDashboard {
  projectCount: number;
  ongoing: Array<{
    id: string;
    name: string;
    code: string;
    color: string;
    health: string;
    methodology: string;
    endDate?: string;
    progressPercent: number;
    taskCount: number;
    doneCount: number;
    overdue: number;
    blocked: number;
    budgetBurn: number;
  }>;
  members: Array<{
    userId: string;
    name: string;
    jobTitle?: string;
    avatarColor?: string;
    employmentType?: string;
    open: number;
    overdue: number;
    blocked: number;
    dueThisWeek: number;
    estimatedHours: number;
    weeksOfWork: number;
    awaitingApprovalHours: number;
  }>;
  blocked: Task[];
  overdue: Task[];
  defectsBySeverity: Record<string, number>;
  approvals: { people: number; hours: number };
  totals: { open: number; overdue: number; blocked: number; stretched: number };
}

export const useTeamDashboard = (memberIds?: string[], enabled = true) =>
  useQuery({
    queryKey: ['dashboard', 'team', memberIds ?? []],
    queryFn: () =>
      get<TeamDashboard>('/dashboards/team', {
        memberIds: memberIds?.length ? memberIds.join(',') : undefined,
      }),
    enabled,
  });

// ---------- Reports ----------

export interface ReportFilters {
  from?: string;
  to?: string;
  workspaceId?: string;
  projectId?: string;
  clientId?: string;
  groupBy?: 'project' | 'person' | 'client';
}

export interface ReportColumn {
  key: string;
  label: string;
  type?: string;
  align?: 'left' | 'right';
  link?: string;
  /** What this number decomposes into, and how to filter down to it. */
  drilldown?: { kind: 'tasks' | 'time'; params: Record<string, string> };
}

export interface ReportResult {
  key: string;
  title: string;
  columns: ReportColumn[];
  rows: Array<Record<string, unknown>>;
  totals?: Record<string, unknown>;
  series?: Array<{ name: string; data: Array<Record<string, unknown>> }>;
  summary?: Array<{ label: string; value: string; tone?: 'success' | 'warning' | 'error' }>;
}

/**
 * The reports this user may run.
 *
 * Fetched rather than derived client-side from the permission list: the server
 * authorises against the same catalogue, so one source decides both what is
 * offered and what is allowed.
 */
export const useReportCatalogue = () =>
  useQuery({
    queryKey: ['reportCatalogue'],
    queryFn: () => get<ReportDefinition[]>('/reports/catalogue'),
    staleTime: 5 * 60 * 1000,
  });

export const useReport = (key: string, filters: ReportFilters) =>
  useQuery({
    queryKey: ['report', key, filters],
    queryFn: () => get<ReportResult>(`/reports/${key}`, filters as Record<string, unknown>),
    enabled: !!key,
  });

export const useExportReport = () =>
  useMutation({
    mutationFn: async ({ key, filters }: { key: string; filters: ReportFilters }) => {
      const response = await api.get(`/reports/${key}/export.csv`, {
        params: filters,
        responseType: 'blob',
      });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = `${key}-${new Date().toISOString().slice(0, 10)}.csv`;
      document.body.appendChild(link);
      link.click();
      link.remove();
      URL.revokeObjectURL(url);
    },
  });

export const useTaskDrilldown = (params?: Record<string, string>) =>
  useQuery({
    queryKey: ['taskDrilldown', params ?? {}],
    queryFn: () =>
      get<{ tasks: Task[]; total: number; estimatedHours: number; actualHours: number }>(
        '/reports/drilldown/tasks',
        params,
      ),
    enabled: !!params,
  });

// ---------- Report pages ----------

export interface ReportPageWidget {
  id: string;
  reportKey: string;
  title?: string;
  width: number;
  order: number;
  display: string;
  presetKey?: string;
  result?: ReportResult;
  restricted?: boolean;
  error?: string;
}

export interface ReportPageSummary {
  id: string;
  name: string;
  description?: string;
  color: string;
  ownerId: { id: string; firstName: string; lastName: string; avatarColor: string } | string;
  projectId?: { id: string; name: string; code: string } | string;
  widgets: Array<{ reportKey: string }>;
  sharedWithUserIds: string[];
  sharedWithOrg: boolean;
}

export interface RenderedReportPage {
  id: string;
  name: string;
  description?: string;
  color: string;
  projectId?: string;
  isOwner: boolean;
  sharedWithOrg: boolean;
  sharedWithUserIds: string[];
  widgets: ReportPageWidget[];
}

export const useReportPages = (projectId?: string) =>
  useQuery({
    queryKey: ['reportPages', projectId ?? 'all'],
    // `get` takes query params directly — an axios-style `{ params }` wrapper
    // would be sent as a literal `params` key and the filter would be ignored.
    queryFn: () => get<ReportPageSummary[]>('/report-pages', projectId ? { projectId } : undefined),
  });

export const useReportPage = (id?: string) =>
  useQuery({
    queryKey: ['reportPage', id],
    queryFn: () => get<RenderedReportPage>(`/report-pages/${id}`),
    enabled: !!id,
  });

const useReportPageMutation = <TArgs,>(request: (args: TArgs) => Promise<unknown>) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: request,
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['reportPages'] });
      qc.invalidateQueries({ queryKey: ['reportPage'] });
    },
  });
};

export const useCreateReportPage = () =>
  useReportPageMutation<Record<string, unknown>>((body) => post('/report-pages', body));

export const useUpdateReportPage = () =>
  useReportPageMutation<{ id: string } & Record<string, unknown>>(({ id, ...body }) =>
    patch(`/report-pages/${id}`, body),
  );

export const useShareReportPage = () =>
  useReportPageMutation<{ id: string; userIds?: string[]; sharedWithOrg?: boolean }>(
    ({ id, ...body }) => post(`/report-pages/${id}/share`, body),
  );

export const useDeleteReportPage = () =>
  useReportPageMutation<string>((id) => del(`/report-pages/${id}`));

// ---------- Comments, attachments, activity ----------

export const useComments = (entityType: string, entityId?: string) =>
  useQuery({
    queryKey: qk.comments(entityType, entityId!),
    queryFn: () =>
      get<Paginated<Comment>>(`/comments/${entityType}/${entityId}`, { sortDir: 'asc' }),
    enabled: !!entityId,
  });

export const useCreateComment = (entityType: string, entityId: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (body: string) => post<Comment>('/comments', { entityType, entityId, body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.comments(entityType, entityId) });
      if (entityType === 'task') qc.invalidateQueries({ queryKey: qk.task(entityId) });
    },
  });
};

export const useAttachments = (entityType: string, entityId?: string) =>
  useQuery({
    queryKey: qk.attachments(entityType, entityId!),
    queryFn: () => get<Attachment[]>(`/attachments/${entityType}/${entityId}`),
    enabled: !!entityId,
  });

/**
 * Uploads a file through the API.
 *
 * Two steps: reserve the record, then send the bytes to it. The API streams
 * them on to object storage without buffering, so the cost of a large upload is
 * a held connection rather than memory.
 *
 * It used to go straight to storage on a presigned URL, which is cheaper for
 * the API and only works where the browser can reach storage directly. In the
 * cluster it cannot, and publishing object storage to make it possible is a
 * larger thing to own than a proxied stream.
 */
export const useUploadAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async ({
      entityType,
      entityId,
      file,
    }: {
      entityType: string;
      entityId: string;
      file: File;
    }) => {
      const { attachmentId } = await post<{ attachmentId: string }>('/attachments', {
        entityType,
        entityId,
        fileName: file.name,
        contentType: file.type || 'application/octet-stream',
        sizeBytes: file.size,
      });

      /**
       * The bytes, straight to the API.
       *
       * Sent as `application/octet-stream` so no body parser on the way in is
       * tempted to read the stream — a JSON file would otherwise be parsed and
       * consumed before it reached the handler. The real type travels beside it
       * and was already declared above.
       */
      return api.put(`/attachments/${attachmentId}/content`, file, {
        headers: {
          'Content-Type': 'application/octet-stream',
          'X-Content-Type': file.type || 'application/octet-stream',
        },
      });
    },
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: qk.attachments(variables.entityType, variables.entityId) });
      // An upload is recorded on the timeline, which is a tab away from the one
      // the file was dropped on.
      qc.invalidateQueries({ queryKey: qk.activity(variables.entityType, variables.entityId) });
      if (variables.entityType === 'task') {
        qc.invalidateQueries({ queryKey: qk.task(variables.entityId) });
      }
    },
  });
};

/**
 * A blob URL for showing a file rather than saving it.
 *
 * The API serves the bytes and the request needs our token, so an <img src>
 * pointed at the endpoint would arrive unauthenticated. Fetching it here and
 * wrapping the result in a blob URL keeps the auth on the request and gives the
 * element something it can render.
 *
 * The URL is revoked when the query leaves the cache — a blob URL pins its data
 * in memory until someone lets go of it, and a gallery of unrevoked previews is
 * a leak that grows with every tile opened.
 */
export const useAttachmentPreview = (id?: string, enabled = true) =>
  useQuery({
    queryKey: ['attachmentPreview', id],
    queryFn: async () => {
      const response = await api.get(`/attachments/${id}/preview`, { responseType: 'blob' });
      const blob = response.data as Blob;
      return { url: URL.createObjectURL(blob), contentType: blob.type };
    },
    enabled: !!id && enabled,
    staleTime: 45 * 60 * 1000,
    gcTime: 50 * 60 * 1000,
    retry: false,
  });

/**
 * Fetches the file and hands it to the browser to save.
 *
 * Through a blob and an anchor rather than by opening a URL. `window.open`
 * after an await is swallowed by the popup blocker — the click that started it
 * no longer counts as user activation once a round trip has completed — and the
 * button did nothing at all, with no error to show for it.
 */
export const useDownloadAttachment = () =>
  useMutation({
    mutationFn: async (id: string) => {
      // Through axios so the request carries our token — the API serves the
      // bytes itself, which means this is an authenticated call and not a link
      // anyone holding it could follow.
      const response = await api.get(`/attachments/${id}/download`, { responseType: 'blob' });
      const url = URL.createObjectURL(response.data as Blob);
      const link = document.createElement('a');
      link.href = url;
      link.download = fileNameFrom(response.headers['content-disposition']) ?? 'download';
      document.body.appendChild(link);
      link.click();
      link.remove();
      // Same tick is safe: the click has already handed the blob to the browser.
      URL.revokeObjectURL(url);
    },
  });

/**
 * The name the server gave the file.
 *
 * `filename*` first: it is the RFC 5987 form and the only one that survives a
 * non-ASCII name, which the plain `filename` carries as underscores.
 */
function fileNameFrom(header?: string): string | undefined {
  if (!header) return undefined;
  const encoded = /filename*=UTF-8''([^;]+)/i.exec(header);
  if (encoded) {
    try {
      return decodeURIComponent(encoded[1]);
    } catch {
      // A malformed value is not worth failing a download over.
    }
  }
  return /filename="?([^";]+)"?/i.exec(header)?.[1];
}

export const useDeleteAttachment = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id }: { id: string; entityType: string; entityId: string }) =>
      del(`/attachments/${id}`),
    onSuccess: (_result, variables) => {
      qc.invalidateQueries({ queryKey: qk.attachments(variables.entityType, variables.entityId) });
      // Removing a file is recorded on the timeline, the same as adding one.
      qc.invalidateQueries({ queryKey: qk.activity(variables.entityType, variables.entityId) });
      if (variables.entityType === 'task') {
        qc.invalidateQueries({ queryKey: qk.task(variables.entityId) });
      }
    },
  });
};

export const useActivity = (entityType: string, entityId?: string) =>
  useQuery({
    queryKey: qk.activity(entityType, entityId!),
    queryFn: () => get<Paginated<ActivityEntry>>(`/audit/${entityType}/${entityId}`),
    enabled: !!entityId,
  });

// ---------- Time tracking ----------

export const useRunningTimer = () =>
  useQuery({
    queryKey: qk.timer,
    queryFn: () => get<TimeEntry | null>('/time-entries/timer'),
    // Keeps the header timer honest if it was started in another tab.
    refetchInterval: 60_000,
  });

export const useStartTimer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { projectId: string; taskId?: string; description?: string }) =>
      post<TimeEntry>('/time-entries/timer/start', {
        ...payload,
        /**
         * Which day this belongs to, decided here.
         *
         * The instants are unambiguous, but the calendar day is not, and the
         * server's own day is nobody's: on a UTC container a timer started at
         * 2am in Chennai was filed to the day before, landing on a row of a
         * week that may already have been submitted.
         */
        date: todayInZone(),
      }),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.timer }),
  });
};

export const useStopTimer = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: () => post<TimeEntry>('/time-entries/timer/stop'),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.timer });
      // A stopped timer *is* a time entry, so everything that reads one has to
      // hear about it — this used to refresh four of the eleven.
      invalidateTime(qc);
    },
  });
};

export interface TimesheetFilters {
  userId?: string;
  projectId?: string;
  isBillable?: boolean;
}

export const useTimesheet = (from: string, to: string, filters: TimesheetFilters = {}) =>
  useQuery({
    queryKey: ['timesheet', from, to, filters],
    queryFn: () => get<Timesheet>('/time-entries/timesheet', { from, to, ...filters }),
  });

export interface ProjectTimesheetRow {
  userId: string;
  name: string;
  avatarColor: string;
  employmentType: string;
  jobTitle?: string;
  days: number[];
  totalMinutes: number;
  billableMinutes: number;
  cost?: number;
  revenue?: number;
}

export interface ProjectTimesheet {
  projectId: string;
  from: string;
  to: string;
  days: string[];
  dailyTotals: number[];
  rows: ProjectTimesheetRow[];
  totalMinutes: number;
}

export const useProjectTimesheet = (projectId?: string, from?: string, to?: string) =>
  useQuery({
    queryKey: ['projectTimesheet', projectId, from, to],
    queryFn: () =>
      get<ProjectTimesheet>(`/time-entries/project-timesheet/${projectId}`, { from, to }),
    enabled: !!projectId && !!from && !!to,
  });

export const useLogTime = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<TimeEntry>('/time-entries', payload),
    onSuccess: () => invalidateTime(qc),
  });
};

export interface TimeEntryFilters {
  taskId?: string;
  projectId?: string;
  userId?: string;
  from?: string;
  to?: string;
  pageSize?: number;
}

export const useTimeEntries = (filters: TimeEntryFilters = {}) =>
  useQuery({
    queryKey: qk.timeEntries(filters),
    queryFn: () => get<Paginated<TimeEntry>>('/time-entries', filters),
  });

/**
 * Everything a time entry is read through.
 *
 * One list, used by every mutation that touches time. There were four
 * hand-written lists before and they disagreed: editing an entry inside the
 * drill-down refreshed the grid behind it and not the panel doing the editing,
 * because `timeDrilldown` appeared in none of them. A panel that will not show
 * the change you just made in it is the worst version of this bug, since the
 * obvious reading is that the save failed.
 *
 * Prefixes, not exact keys — each of these is also keyed by its own filters, so
 * a partial match is what reaches the one being looked at.
 */
const TIME_DERIVED_CACHES = [
  'timeEntries',
  'timesheet',
  'projectTimesheet',
  // The two drill-downs: the timesheet's own, and the one reports open.
  'timeDrilldown',
  'reportTimeDrilldown',
  // Hours waiting on a signature, and the week behind one of them.
  'approvals',
  'approvalDetail',
  // Logged time changes a task's actual hours and every ancestor's rollup.
  'task',
  'taskTree',
  'dashboard',
  // Utilisation, profitability and the rest are all counted from time.
  'report',
];

const invalidateTime = (qc: ReturnType<typeof useQueryClient>) => {
  for (const key of TIME_DERIVED_CACHES) qc.invalidateQueries({ queryKey: [key] });
};

export const useUpdateTimeEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<TimeEntry>(`/time-entries/${id}`, payload),
    onSuccess: () => invalidateTime(qc),
  });
};

export const useDeleteTimeEntry = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/time-entries/${id}`),
    onSuccess: () => invalidateTime(qc),
  });
};

export const useSubmitTimesheet = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (weekStart: string) =>
      post<{ submitted: number }>('/time-entries/submit', { weekStart }),
    // Submitting changes the status on every entry in the week, and status is
    // shown on the rows, in both drill-downs and in the approval queue.
    onSuccess: () => invalidateTime(qc),
  });
};

export const useUtilisation = (from: string, to: string, enabled = true) =>
  useQuery({
    queryKey: qk.utilisation(from, to),
    queryFn: () => get<UtilisationRow[]>('/time-entries/reports/utilisation', { from, to }),
    enabled,
  });

// ---------- Dashboards ----------

export const useMyDashboard = (enabled = true) =>
  useQuery({
    queryKey: qk.dashboardMe,
    queryFn: () => get<MyDashboard>('/dashboards/me'),
    enabled,
  });

export const useExecutiveDashboard = (enabled = true) =>
  useQuery({
    queryKey: qk.dashboardExec,
    queryFn: () => get<ExecutiveDashboard>('/dashboards/executive'),
    enabled,
  });

export const useProjectDashboard = (projectId?: string) =>
  useQuery({
    queryKey: qk.dashboardProject(projectId!),
    queryFn: () => get<ProjectDashboard>(`/dashboards/project/${projectId}`),
    enabled: !!projectId,
  });

// ---------- Notifications & search ----------

export const useNotifications = () =>
  useQuery({
    queryKey: qk.notifications,
    queryFn: () =>
      get<Paginated<Notification> & { unreadCount: number }>('/notifications', { pageSize: 20 }),
    refetchInterval: 60_000,
  });

export const useMarkNotificationsRead = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (ids?: string[]) =>
      ids?.length ? post('/notifications/read', { ids }) : post('/notifications/read-all'),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.notifications }),
  });
};

export const useSearch = (term: string) =>
  useQuery({
    queryKey: qk.search(term),
    queryFn: () => get<SearchHit[]>('/search', { q: term }),
    enabled: term.trim().length >= 2,
    staleTime: 30_000,
  });

// ---------- Hierarchical task tree ----------

/** Depth-ordered flat list including any ancestors needed to show a filtered child. */
export const useTaskTree = (filters: TaskFilters = {}, enabled = true) =>
  useQuery({
    queryKey: qk.taskTree(filters),
    queryFn: () => get<{ items: Task[] }>('/tasks/tree', filters),
    enabled,
    placeholderData: keepPreviousData,
  });

export const useAllowedChildTypes = (taskId?: string) =>
  useQuery({
    queryKey: ['allowedChildTypes', taskId],
    queryFn: () => get<string[]>(`/tasks/${taskId}/allowed-child-types`),
    enabled: !!taskId,
  });

// ---------- Project configuration ----------

const useConfigMutation = <T>(projectId: string, path: string) => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: T) => put<Project>(`/projects/${projectId}/config/${path}`, payload),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: qk.project(projectId) });
      // Config changes migrate tasks server-side, so every task view is stale.
      qc.invalidateQueries({ queryKey: ['board', projectId] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
      qc.invalidateQueries({ queryKey: ['taskTree'] });
    },
  });
};

// `workflow` picks which list the replace targets: defects keep their own
// statuses and types, and the endpoint scopes its task migration to match.
export const useReplaceStatuses = (projectId: string) =>
  useConfigMutation<{ statuses: unknown[]; reassignRemovedTo?: string; workflow?: 'tasks' | 'bugs' }>(
    projectId,
    'statuses',
  );
export const useReplaceTypes = (projectId: string) =>
  useConfigMutation<{
    types: unknown[];
    reassignRemovedTo?: string;
    workflow?: 'tasks' | 'bugs';
    bugWorkflowEnabled?: boolean;
  }>(projectId, 'types');
export const useReplacePriorities = (projectId: string) =>
  useConfigMutation<{ priorities: unknown[]; reassignRemovedTo?: string }>(projectId, 'priorities');
export const useReplaceCustomFields = (projectId: string) =>
  useConfigMutation<{ customFields: unknown[]; workflow?: 'tasks' | 'bugs' }>(
    projectId,
    'custom-fields',
  );
export const useReplaceBugEnvironments = (projectId: string) =>
  useConfigMutation<{ options: unknown[] }>(projectId, 'bug-environments');
export const useUpdateHierarchy = (projectId: string) =>
  useConfigMutation<Record<string, unknown>>(projectId, 'hierarchy');

export const useDestroyProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, confirmationCode }: { id: string; confirmationCode: string }) =>
      post(`/projects/${id}/destroy`, { confirmationCode }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: ['projects'] });
      qc.invalidateQueries({ queryKey: ['dashboard'] });
    },
  });
};

export const useArchiveProject = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/projects/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['projects'] }),
  });
};

// ---------- Templates ----------

export const useTemplates = (category?: string) =>
  useQuery({
    queryKey: qk.templates(category),
    queryFn: () => get<ProjectTemplate[]>('/templates', category ? { category } : undefined),
  });

export const useTemplate = (id?: string) =>
  useQuery({
    queryKey: qk.template(id!),
    queryFn: () => get<ProjectTemplate>(`/templates/${id}`),
    enabled: !!id,
  });

export const useSaveAsTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      post<ProjectTemplate>('/templates/from-project', payload),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
};

export const useApplyTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      templateId,
      projectId,
      ...options
    }: {
      templateId: string;
      projectId: string;
      includeTasks?: boolean;
      includeConfig?: boolean;
    }) => post<{ tasksCreated: number }>(`/templates/${templateId}/apply/${projectId}`, options),
    onSuccess: (_data, variables) => {
      qc.invalidateQueries({ queryKey: qk.project(variables.projectId) });
      qc.invalidateQueries({ queryKey: ['board', variables.projectId] });
      qc.invalidateQueries({ queryKey: ['taskTree'] });
      qc.invalidateQueries({ queryKey: ['tasks'] });
    },
  });
};

export const useDeleteTemplate = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/templates/${id}`),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['templates'] }),
  });
};

// ---------- People ----------

export interface PeopleFilters {
  search?: string;
  role?: string;
  employmentType?: string;
  status?: string;
  isActive?: boolean;
  page?: number;
  pageSize?: number;
}

export const usePeople = (filters: PeopleFilters = {}) =>
  useQuery({
    queryKey: qk.people(filters),
    queryFn: () => get<Paginated<Person>>('/users', { pageSize: 100, ...filters }),
    placeholderData: keepPreviousData,
  });

export const usePerson = (id?: string) =>
  useQuery({
    queryKey: ['person', id],
    queryFn: () => get<Person>(`/users/${id}`),
    enabled: !!id,
  });

const invalidatePeople = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['people'] });
  qc.invalidateQueries({ queryKey: qk.directory });
};

export const useInviteUser = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) =>
      post<{
        userId: string;
        /** `emailed` says whether the message actually left — the link is
            returned either way, as the fallback when mail is down. */
        invitation: { link: string; expiresAt: string; emailed?: boolean };
      }>(
        '/users/invite',
        payload,
      ),
    onSuccess: () => invalidatePeople(qc),
  });
};

export const useResendInvite = () =>
  useMutation({
    mutationFn: (userId: string) =>
      post<{ link: string; expiresAt: string; emailed?: boolean }>(
        `/users/${userId}/resend-invite`,
      ),
  });

export const useUpdatePerson = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<Person>(`/users/${id}`, payload),
    onSuccess: (_data, variables) => {
      invalidatePeople(qc);
      qc.invalidateQueries({ queryKey: ['person', variables.id] });
    },
  });
};

export const useDeactivatePerson = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/users/${id}`),
    onSuccess: () => invalidatePeople(qc),
  });
};

export const useReactivatePerson = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post(`/users/${id}/reactivate`),
    onSuccess: () => invalidatePeople(qc),
  });
};

export const useDestroyPerson = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => post(`/users/${id}/destroy`),
    onSuccess: () => invalidatePeople(qc),
  });
};

export const useUpdatePreferences = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (patchBody: Record<string, unknown>) =>
      patch<Record<string, unknown>>('/users/me/preferences', patchBody),
    onSuccess: () => qc.invalidateQueries({ queryKey: qk.me }),
  });
};

// ---------- Clients ----------

export const useClientList = (search?: string) =>
  useQuery({
    queryKey: ['clientList', search ?? ''],
    queryFn: () => get<Paginated<Client>>('/clients', { pageSize: 200, search: search || undefined }),
  });

const invalidateClients = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: ['clientList'] });
  qc.invalidateQueries({ queryKey: qk.clients });
};

export const useCreateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<Client>('/clients', payload),
    onSuccess: () => invalidateClients(qc),
  });
};

export const useUpdateClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<Client>(`/clients/${id}`, payload),
    onSuccess: () => invalidateClients(qc),
  });
};

export const useDeleteClient = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/clients/${id}`),
    onSuccess: () => invalidateClients(qc),
  });
};

// ---------- Workspaces ----------

const invalidateWorkspaces = (qc: ReturnType<typeof useQueryClient>) => {
  qc.invalidateQueries({ queryKey: qk.workspaces });
  qc.invalidateQueries({ queryKey: ['projects'] });
};

export const useCreateWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: Record<string, unknown>) => post<Workspace>('/workspaces', payload),
    onSuccess: () => invalidateWorkspaces(qc),
  });
};

export const useUpdateWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ id, ...payload }: { id: string } & Record<string, unknown>) =>
      patch<Workspace>(`/workspaces/${id}`, payload),
    onSuccess: () => invalidateWorkspaces(qc),
  });
};

export const useDeleteWorkspace = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => del(`/workspaces/${id}`),
    onSuccess: () => invalidateWorkspaces(qc),
  });
};

/** Upserts membership — the API updates access when the person is already a member. */
export const useAddWorkspaceMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({
      workspaceId,
      ...payload
    }: {
      workspaceId: string;
      userId: string;
      access: string;
    }) => post<Workspace>(`/workspaces/${workspaceId}/members`, payload),
    onSuccess: () => invalidateWorkspaces(qc),
  });
};

export const useRemoveWorkspaceMember = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: ({ workspaceId, userId }: { workspaceId: string; userId: string }) =>
      del<Workspace>(`/workspaces/${workspaceId}/members/${userId}`),
    onSuccess: () => invalidateWorkspaces(qc),
  });
};

// ---------- Bug tracking ----------

export interface BugMetrics {
  summary: {
    total: number;
    open: number;
    resolved: number;
    reopened: number;
    regressions: number;
    overdue: number;
    resolutionRate: number;
  };
  severity: Record<string, { open: number; resolved: number }>;
  environments: Array<{ key: string; count: number }>;
  resolutions: Array<{ key: string; count: number }>;
  ageing: { openAvgDays: number; openMaxDays: number; resolvedAvgDays: number };
}

export interface DefectPersonRow {
  userId: string | null;
  name: string;
  avatarColor: string;
  total: number;
  open: number;
  resolved: number;
  critical: number;
  reopened: number;
  avgAgeDays: number;
  reopenRate: number;
}

export interface DefectBreakdown {
  byType: Array<{
    type: string;
    total: number;
    open: number;
    resolved: number;
    critical: number;
    reopened: number;
    avgAgeDays: number;
  }>;
  byAssignee: DefectPersonRow[];
  byReporter: DefectPersonRow[];
}

export const useDefectBreakdown = (projectId?: string) =>
  useQuery({
    queryKey: ['defectBreakdown', projectId],
    queryFn: () => get<DefectBreakdown>(`/tasks/defect-breakdown/${projectId}`),
    enabled: !!projectId,
  });

export const useBugMetrics = (projectId?: string) =>
  useQuery({
    queryKey: ['bugMetrics', projectId],
    queryFn: () => get<BugMetrics>(`/tasks/bug-metrics/${projectId}`),
    enabled: !!projectId,
  });

// ---------- Timesheet approvals ----------

export const usePendingApprovals = (filters: { employmentType?: string; userId?: string } = {}) =>
  useQuery({
    queryKey: qk.approvals(filters),
    queryFn: () => get<ApprovalRow[]>('/time-entries/approvals', filters),
  });

export const useApprovalDetail = (userId?: string, week?: string) =>
  useQuery({
    queryKey: qk.approvalDetail(userId!, week!),
    queryFn: () => get<TimeEntry[]>(`/time-entries/approvals/${userId}/${week}`),
    enabled: !!userId && !!week,
  });

/**
 * An approver correcting what a logger marked billable.
 *
 * Kept separate from the approve/reject decision because it is a different
 * judgement — on maintenance work the person logging the hour often cannot
 * know whether the contract covers it.
 */
export const useOverrideBillable = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: { entryIds: string[]; isBillable: boolean; note?: string }) =>
      post<{ updated: number; skipped: number }>('/time-entries/billable', payload),
    onSuccess: () => {
      invalidateTime(qc);
      // What is chargeable is what an invoice is drawn from.
      qc.invalidateQueries({ queryKey: ['billingExport'] });
    },
  });
};

export const useDecideTime = () => {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: (payload: {
      entryIds: string[];
      decision: 'approved' | 'rejected';
      rejectionReason?: string;
    }) => post<{ updated: number }>('/time-entries/decide', payload),
    // A decision changes an entry's status, which is rendered on the rows, in
    // both drill-downs and on the task it belongs to.
    onSuccess: () => invalidateTime(qc),
  });
};
